import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { formatRelativeDays, pluralize } from '../lib/format.js';
import { Avatar, Button, EmptyState } from '../components/ui.js';
import { SkeletonLists } from '../components/Skeleton.js';
import { IconFriends } from '../components/icons.js';
import { ListMosaic } from '../components/ListMosaic.js';

/**
 * Списки друзей — экран «кому что подарить».
 *
 * Сортировка по дате события, а не по алфавиту: если у кого-то день рождения
 * на этой неделе, он должен быть сверху, иначе экран бесполезен.
 */

export function FeedPage(): JSX.Element {
  const { data: lists, isLoading } = useQuery({
    queryKey: ['lists', 'feed'],
    queryFn: api.lists.feed,
  });

  const { data: birthdays } = useQuery({
    queryKey: ['friends', 'birthdays'],
    queryFn: api.friends.birthdays,
    // Дата рождения меняется раз в жизни — незачем спрашивать её часто.
    staleTime: 60 * 60 * 1000,
  });

  /* Шапка данных не ждёт — пишем один раз и показываем в обоих состояниях. */
  const шапка = (
    <div className="page-header">
      <div>
        <h1>Что подарить друзьям</h1>
        <p className="muted small">Списки тех, кого вы добавили в друзья. Бронь останется тайной для получателя.</p>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <>
        {шапка}
        <SkeletonLists />
      </>
    );
  }

  const sorted = [...(lists ?? [])].sort((a, b) => {
    // Списки с ближайшей датой — наверх; без даты — в конец.
    if (a.eventDate && b.eventDate) return a.eventDate.localeCompare(b.eventDate);
    if (a.eventDate) return -1;
    if (b.eventDate) return 1;
    return b.updatedAt.localeCompare(a.updatedAt);
  });

  return (
    <>
      {шапка}

      {/*
        Напоминание о ближайших праздниках. Дата рождения у нас и так есть,
        но без такой подсказки о ней узнают постфактум — а подарок хорош
        вовремя, а не «извини, я забыл».
      */}
      {(birthdays ?? []).length > 0 && (
        <div className="stack stack--sm" style={{ marginBottom: 'var(--space-5)' }}>
          {(birthdays ?? []).slice(0, 4).map((entry) => (
            <div key={entry.user.id} className="notice notice--info">
              <Avatar name={entry.user.displayName} src={entry.user.avatarUrl} size="sm" />
              <div className="grow">
                <span className="small">
                  <strong>{entry.user.displayName}</strong> — день рождения{' '}
                  {entry.daysUntil === 0
                    ? 'сегодня 🎉'
                    : entry.daysUntil === 1
                      ? 'завтра'
                      : `через ${pluralize(entry.daysUntil, 'день', 'дня', 'дней')}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {sorted.length === 0 ? (
        <EmptyState
          icon={<IconFriends />}
          title="Пока нечего показать"
          text="Добавьте друзей — и здесь появятся их списки желаний. Тогда не придётся гадать, что дарить."
          action={
            <Link to="/friends">
              <Button variant="primary">Найти друзей</Button>
            </Link>
          }
        />
      ) : (
        <div className="lists-grid">
          {/*
            Карточки те же, что и на своей главной: человек ходит между этими
            экранами постоянно, и разная разметка для одного и того же предмета
            заставляла бы каждый раз заново искать глазами название.

            Отличие одно и по делу: здесь на первом месте лицо друга, а не
            обложка списка. Выбирают тут не список, а кому дарить.
          */}
          {sorted.map((list) => (
            <Link key={list.id} to={`/lists/${list.id}`} className="list-card">
              <span className="list-card__owner">
                <Avatar name={list.owner.displayName} src={list.owner.avatarUrl} />
                <span className="truncate">{list.owner.displayName}</span>
              </span>

              {/*
                Та же мозаика, что и на своей странице списков: выбирая
                подарок, человек смотрит на вещи, а не на названия списков.
                Смайлик показывает сама мозаика, поэтому из заголовка он ушёл —
                иначе он стоял бы дважды.
              */}
              <ListMosaic list={list} />

              <span className="list-card__title truncate">{list.title}</span>

              <span className="list-card__count">
                {pluralize(list.itemCount, 'желание', 'желания', 'желаний')}
              </span>

              {list.eventDate && (
                <span className="list-card__meta">
                  <span className="badge badge--warning">{formatRelativeDays(list.eventDate)}</span>
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
